import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MatDialog } from '@angular/material/dialog';
import { SaveAreaDialogComponent } from './components/save-area-dialog/save-area-dialog.component';
import { FeatureGeometry } from '../../models/area-item.model';
import { MapCommunicatorService } from './map-communicator.service';
import { Project } from '../../models/project.module';
import {
  AreaQuickReportDialogComponent
} from './components/area-quick-report-dialog/area-quick-report-dialog.component';
import { IndexedDbService } from '../../services/dbService.service';
import { Router } from '@angular/router';
import { AreaListComponent } from './components/area-list/area-list.component';
import { LayerSidebarComponent } from './components/layer-sidebar/layer-sidebar.component';
import { MatTooltip } from '@angular/material/tooltip';
import { NgClass, NgStyle } from '@angular/common';
import { InlineSvgComponent } from '../../components/inline-svg/inline-svg.component';
import { AddressSearchComponent } from './components/address-search/address-search.component';
import { MapComponent } from './components/map/map.component';
import { BookmarksSidebarComponent } from './components/bookmarks-sidebar/bookmarks-sidebar.component';
import { Subject, takeUntil } from 'rxjs';
import { Selectedlayer } from '../../models/layer.models';
import {
  CreateBookmarkModalComponent
} from './components/bookmarks-sidebar/create-bookmark-modal/create-bookmark-modal.component';
import { Bookmark } from '../../models/bookmark.model';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { ConfirmationDialogComponent } from '../../components/confirmation-dialog/confirmation-dialog.component';
import { HttpErrorResponse } from '@angular/common/http';
import { BookmarkService } from '../../services/bookmark.service';
import { ToastNotificationService } from '../../services/toast-notification.service';
import { User } from '../../models/user.model';

@Component({
  selector: 'frmg-risk-map',
  templateUrl: './risk-map.component.html',
  styleUrls: ['./risk-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideInOut', [
      state('in', style({
        transform: 'translate3d(0,0,0)'
      })),
      state('out', style({
        transform: 'translate3d(-100%, 0, 0)'
      })),
      transition('in => out', animate('400ms ease-in-out')),
      transition('out => in', animate('400ms ease-in-out'))
    ])
  ],
  imports: [AreaListComponent, LayerSidebarComponent, BookmarksSidebarComponent, MatTooltip, NgClass, InlineSvgComponent, NgStyle, AddressSearchComponent, MapComponent, MatMenu, MatMenuItem, MatMenuTrigger]
})
export class RiskMapComponent implements OnDestroy, OnInit {
  private destroy$ = new Subject<void>();
  public isOpen : 'areas' | 'layers' |  'bookmarks' | 'out' = 'areas';
  public action: string = 'selection';
  public drawTools = ['rectangle', 'circle', 'Polygon', 'point', 'line'];
  public currentToolIcon = `${this.drawTools[2]}`;
  public currentDrawn: FeatureGeometry | null;
  public error: boolean;
  public currentProject: Project;
  public previewArray = [];
  public drawing: boolean;
  public bookmarkActive: boolean = false;
  public bookmark: Bookmark;
  public savedState: any;
  private subPath: string = '';
  private routerSubscription: any;
  public layersState: Selectedlayer[] = [];
  public bbox: number[];
  public sidebarsToOpen: {
    name: 'areas' | 'layers' |  'bookmarks' | 'out',
    tooltip: string,
    src: string
  }[] = [
      {
        name: 'areas',
        tooltip: 'My areas',
        src: 'project.svg'
      },
      {
        name: 'layers',
        tooltip: 'My layers',
        src: 'Layers.svg'
      }
      , {
        name: 'bookmarks',
        tooltip: 'My bookmarks',
        src: 'bookmark.svg'
      }
    ];
  public currentUser: User;

  constructor(private dialog: MatDialog,
              private indexedDbService: IndexedDbService,
              private cd: ChangeDetectorRef,
              private mapCommunicatorService: MapCommunicatorService,
              private bookmarkService: BookmarkService,
              private toastNotificationService: ToastNotificationService,
              private router: Router
  ) {
    this.drawTools.splice(1, 1);
    this.currentUser = JSON.parse(localStorage.getItem('user')) as User;
    this.mapCommunicatorService.mapCommunicator.subscribe(action => {
      if (action.startsWith('map_select_')) {
        if (this.isOpen !== 'areas') this.isOpen = 'areas';
        this.cd.detectChanges();
      }
    });

    this.getPreviews();
  }

  ngOnInit() {
    const url = this.router.url;
    this.subPath = url.replace(/^\/?risk-map\/?/, '') || '';
    if (this.subPath && this.subPath !== '') {
      this.isOpen = 'areas';
    }
    this.mapCommunicatorService.layers
      .pipe(takeUntil(this.destroy$))
      .subscribe((layers) => {
        this.layersState = layers;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.mapCommunicatorService.selectedAreas.next([]);
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  public editBookmark(bookmark: Bookmark, event: Event, bookmarkMenuTrigger: MatMenuTrigger) {
    if (event && (event as any).stopPropagation) {
      (event as any).stopPropagation();
    }
    bookmarkMenuTrigger.closeMenu();
    const createModal = this.dialog.open(CreateBookmarkModalComponent, {
      data: {
        bookmark
      }
    });

    createModal.afterClosed().subscribe(resp => {
      if (resp) {
        this.bookmark.name = resp.name;
        this.cd.detectChanges();
      }
    });
  }

  public bulkDelete(bookmark: Bookmark, bookmarkMenuTrigger: MatMenuTrigger) {
    bookmarkMenuTrigger.closeMenu();
    this.dialog
      .open<ConfirmationDialogComponent>(ConfirmationDialogComponent, {
        data: {
          header: `Are you sure you want to delete ${bookmark ? 'this bookmark' : 'these bookmarks'}?`,
          body: 'It will be removed for all users and won’t be available anymore.',
          confirmText: 'Delete'
        }
      }).afterClosed().subscribe(resp => {
        const filter = {
          filters: ''
        };
        if (bookmark) {
          filter.filters = `id:eq:${bookmark.id}`;
        } else return;

        if (resp) this.bookmarkService.deleteBookmark(filter).subscribe(_ => {
          this.toastNotificationService.showNotification({ message: 'Bookmark deleted successfully.', type: 'success' });
          this.bookmark = null;
        }, (error: HttpErrorResponse) => {
          this.toastNotificationService.showNotification({ message: error.error.detail, type: 'error' });
        });
      });
  }

  public activateBookmark(bookmark: Bookmark) {
    this.bookmarkActive = true;
    this.isOpen = 'areas';
    this.bookmark = bookmark;
  }

  private getPreviews(key?: string) {
    this.indexedDbService.getAllKeys('skyforest', 'quickReport').subscribe(resp => {
      this.previewArray = resp;
      if (key) {
        const index = this.previewArray.indexOf(key);
        if (index > -1) this.discardPreview(index, key);
      }
      this.cd.detectChanges();
    });
  }


  public toggle(state: 'areas' | 'layers' |  'bookmarks' | 'out') {
    if (this.isOpen === state) {
      this.isOpen = 'out';
    } else this.isOpen = state;
  }

  public setDrawTool(type: string): void {
    const action = `draw_${type}`;
    this.mapCommunicatorService.mapCommunicator.next(action);
    this.action = action;
    this.currentToolIcon = `${type}`;
    this.drawing = true;
  }

  public selectionState(): void {
    this.mapCommunicatorService.mapCommunicator.next('selection');
    this.action = 'selection';
    this.drawing = false;
  }

  public rectangleSelectState(): void {
    this.mapCommunicatorService.mapCommunicator.next('rectangleSelect');
    this.action = 'rectangleSelect';
    this.drawing = false;
  }

  public saveDrawnAreas(): void {
    if (!this.currentDrawn || this.error) return;
    const layer: FeatureGeometry = this.currentDrawn;
    const saveDialog = this.dialog.open(SaveAreaDialogComponent, {
      data: {
        layer,
        project: this.currentProject
      }
    });

    saveDialog.afterClosed().subscribe(resp => {
      if (resp) {
        this.selectionState();
        this.clearDrawnAreas();
      }
    });
  }

  public quickArea(): void {
    if (!this.currentDrawn || this.error || this.currentDrawn.type !== 'Point') return;
    const layer: FeatureGeometry = this.currentDrawn;
    const quickReportDialog = this.dialog.open(AreaQuickReportDialogComponent, {
      disableClose: true,
      data: {
        layer,
        project: this.currentProject
      },
      enterAnimationDuration: '200ms',
      exitAnimationDuration: '200ms'
    }
    );
    //
    quickReportDialog.afterClosed().subscribe(resp => {
      const key = resp && `${layer.coordinates[0]}, ${layer.coordinates[1]}`;
      this.getPreviews(key);
    });
  }

  public clearDrawnAreas(): void {
    if (this.currentProject?.path == 'root') return;
    this.mapCommunicatorService.mapCommunicator.next('clear');
    this.currentDrawn = null;
    this.drawing = false;
    setTimeout(_ => {
      this.cd.detectChanges();
    }, 250);
  }

  public saveBookmark(): void {
    if (!this.currentProject) return;
    const createModal = this.dialog.open(CreateBookmarkModalComponent, {
      data: {
        project: this.currentProject,
        layers: this.layersState,
        bbox: this.bbox
      }
    });
    createModal.afterClosed().subscribe(resp => {
      if (resp) {
        this.mapCommunicatorService.mapCommunicator.next('saveBookmark');
      }
    });
  }

  public openDrawTools(): void {
    if (this.action !== 'drawTool') {
      this.action = 'drawTool';
    } else this.action = '';
  }

  public onDrawEnd(body: {
    feature: FeatureGeometry | null,
    error: boolean
  }): void {
    this.currentDrawn = body.feature;
    this.error = body.error;
  }

  public projectSelect(project: Project | null): void {
    this.currentProject = project;
  }

  public capitalizeFirstLetter(string: string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  public zoomMap(type: string) {
    this.mapCommunicatorService.mapCommunicator.next(`zoom_${type}`);
  }

  public saveListState(state: any) {
    this.savedState = state;
  }

  public discardPreview(index: number, key: string, $event?) {
    $event && $event.preventDefault();
    this.indexedDbService.deleteData(key, 'skyforest', 'quickReport').then(_ => {
      this.previewArray.splice(index, 1);
      this.cd.detectChanges();
    });
  }

  public openPreview(key: string, index: number, $event) {
    $event.preventDefault();
    this.dialog.open(AreaQuickReportDialogComponent, {
      disableClose: true,
      data: {
        layer: { coordinates: key.split(', ') },
        project: this.currentProject
      },
      enterAnimationDuration: '200ms',
      exitAnimationDuration: '200ms'
    }
    ).afterClosed().subscribe(resp => {
      if (resp) this.discardPreview(index, key, $event);
    });
  }

  public bboxChanged(bbox: number[]) {
    if (this.bookmark) {
      this.bookmarkActive = this.bbox === this.bookmark.bbox;
    }
    this.bbox = bbox;
  }
}
